import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Container, Card, Alert } from 'react-bootstrap';
import { SettingsPage } from '../components/settings/settings-page';
import { Section } from '../models';
import { MarkersOutsideOptimalDisplay } from '../models/UserProfile';
import { UnitSystem } from '../models/units/unitConversions';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { UserStore } from '../stores/user-store';
import { LoadingStore } from '../stores/loading-store';
import { profileSaveErrorMessage } from '../utils/profileSaveError';

interface SettingsContainerProps {
  userProfileUIStore?: UserProfileUIStore;
  userStore?: UserStore;
  loadingStore?: LoadingStore;
}

interface SettingsContainerState {
  successMessage: string | null;
  errorMessage: string | null;
}

@inject('userProfileUIStore', 'userStore', 'loadingStore')
@observer
export class SettingsContainer extends React.Component<
  SettingsContainerProps,
  SettingsContainerState
> {
  state: SettingsContainerState = {
    successMessage: null,
    errorMessage: null,
  };

  componentDidMount() {
    const { userProfileUIStore, userStore } = this.props;

    // Load current user and user profile if not already loaded
    if (!userStore?.currentUser?.subject) {
      userStore?.loadCurrentUser();
    }

    if (!userProfileUIStore?.currentUserProfile) {
      userProfileUIStore?.loadCurrentUserProfile();
    }
  }

  /**
   * The byline is handed over on Save rather than written as it is typed, the
   * way every other field on this page is. The logo below it saves the profile
   * the moment a file lands, and with push-on-change that upload would persist
   * a byline the practitioner was still typing.
   */
  handleSavePreparedBy = async (preparedBy: string) => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) return;

    try {
      userProfileUIStore.updatePreparedBy(preparedBy);
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Prepared By saved.',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving Prepared By:', error);
      this.setState({
        errorMessage: profileSaveErrorMessage(error, 'Error saving Prepared By.'),
        successMessage: null,
      });
    }
  };

  /**
   * Both cover-page headings, saved together by the one button under them.
   *
   * Handed over on Save rather than written as typed, for the reason above: the
   * logo block between the byline and these fields persists the whole profile
   * on upload, and a title still being typed must not go with it.
   */
  handleSaveReportTitles = async (
    analysisTitle: string,
    comparisonTitle: string,
  ) => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) return;

    try {
      userProfileUIStore.updateReportTitles(analysisTitle, comparisonTitle);
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Report titles saved.',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving report titles:', error);
      this.setState({
        errorMessage: profileSaveErrorMessage(
          error,
          'Error saving report titles.',
        ),
        successMessage: null,
      });
    }
  };

  // The logo saves itself the moment it is uploaded or removed rather than
  // waiting for a Save button. Came here from Profile with Prepared By.
  handleLogoUploaded = async (logoUrl: string) => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) {
      this.setState({ errorMessage: 'Error: user profile not loaded' });
      return;
    }

    try {
      userProfileUIStore.updateLogo(logoUrl);
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Logo uploaded and saved successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving logo URL:', error);
      this.setState({
        errorMessage: profileSaveErrorMessage(
          error,
          'Error saving logo. Please try again.',
        ),
        successMessage: null,
      });
    }
  };

  handleLogoRemoved = async () => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) {
      this.setState({ errorMessage: 'Error: user profile not loaded' });
      return;
    }

    try {
      userProfileUIStore.removeLogo();
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Logo removed successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      this.setState({
        errorMessage: profileSaveErrorMessage(
          error,
          'Error removing logo. Please try again.',
        ),
        successMessage: null,
      });
    }
  };

  handleError = (error: string) => {
    this.setState({ errorMessage: error, successMessage: null });
  };

  handleBrandsChange = (brands: string[]) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateEnabledSupplementBrands(brands);
    }
  };

  handleExcludedFromPdfSectionsChange = (sections: Section[]) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateExcludedFromPdfSections(sections);
    }
  };

  handleMarkersOutsideOptimalDisplayChange = (mode: MarkersOutsideOptimalDisplay) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateMarkersOutsideOptimalDisplay(mode);
    }
  };

  handleShowOnlySectionSignalsChange = (value: boolean) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateShowOnlySectionSignals(value);
    }
  };

  handleShowOnlyImmuneSectionSignalsChange = (value: boolean) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateShowOnlyImmuneSectionSignals(value);
    }
  };

  handleShowImmuneAbsolutesChange = (value: boolean) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateShowImmuneAbsolutes(value);
    }
  };

  handleShowPreviousTestMarkersInComparisonChange = (value: boolean) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateShowPreviousTestMarkersInComparison(value);
    }
  };

  handleUnitSystemChange = (value: UnitSystem) => {
    const { userProfileUIStore } = this.props;
    if (userProfileUIStore) {
      userProfileUIStore.updateUnitSystem(value);
    }
  };

  handleSaveSettings = async () => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) return;

    try {
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Settings saved successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      this.setState({
        errorMessage: profileSaveErrorMessage(error, 'Error saving settings.'),
        successMessage: null,
      });
    }
  };

  dismissAlert = (type: 'success' | 'error') => {
    if (type === 'success') {
      this.setState({ successMessage: null });
    } else {
      this.setState({ errorMessage: null });
    }
  };

  render() {
    const { userProfileUIStore } = this.props;
    const { successMessage, errorMessage } = this.state;

    if (!userProfileUIStore?.currentUserProfile) {
      return (
        <Container className="mt-4">
          <Card>
            <Card.Body>
              <div className="text-center">
                <p>Loading user profile...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      );
    }

    return (
      <Container fluid className="mt-4">
        <Card>
          <Card.Header>Report Settings</Card.Header>
          <Card.Body>
            {successMessage && (
              <Alert
                variant="success"
                dismissible
                onClose={() => this.dismissAlert('success')}
              >
                <div className="text-center m-2">{successMessage}</div>
              </Alert>
            )}

            {errorMessage && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => this.dismissAlert('error')}
              >
                <div className="text-center m-2">{errorMessage}</div>
              </Alert>
            )}

            <SettingsPage
              userProfile={userProfileUIStore.currentUserProfile}
              userProfileUIStore={userProfileUIStore}
              onSavePreparedBy={this.handleSavePreparedBy}
              onSaveReportTitles={this.handleSaveReportTitles}
              onLogoUploaded={this.handleLogoUploaded}
              onLogoRemoved={this.handleLogoRemoved}
              onError={this.handleError}
              onBrandsChange={this.handleBrandsChange}
              onExcludedFromPdfSectionsChange={this.handleExcludedFromPdfSectionsChange}
              onMarkersOutsideOptimalDisplayChange={this.handleMarkersOutsideOptimalDisplayChange}
              onShowOnlySectionSignalsChange={this.handleShowOnlySectionSignalsChange}
              onShowOnlyImmuneSectionSignalsChange={this.handleShowOnlyImmuneSectionSignalsChange}
              onShowImmuneAbsolutesChange={this.handleShowImmuneAbsolutesChange}
              onShowPreviousTestMarkersInComparisonChange={
                this.handleShowPreviousTestMarkersInComparisonChange
              }
              onUnitSystemChange={this.handleUnitSystemChange}
              onSaveSettings={this.handleSaveSettings}
            />
          </Card.Body>
        </Card>
      </Container>
    );
  }
}
